// frontend/src/lib/maintenanceApi.ts

/**
 * API client functions for the Maintenance feature.
 * Provides typed wrappers for all maintenance-related API endpoints.
 */

import type {
  MaintenanceContractTemplate,
  MaintenanceContractTemplateForm,
  MaintenanceContract,
  MaintenanceContractListItem,
  MaintenanceContractForm,
  MaintenanceAppointment,
  MaintenanceAppointmentListItem,
  MaintenanceAppointmentForm,
  MaintenanceAppointmentCalendarItem,
  MaintenancePhoto,
  MaintenancePhotoForm,
  MaintenanceRepairItem,
  MaintenanceRepairItemForm,
  MaintenanceNotificationSettings,
  MaintenanceNotificationSettingsForm,
  GPSCheckinData,
  GPSCheckoutData,
  CreateEstimateResponse,
  PaginatedResponse,
  ContractFilters,
  AppointmentFilters,
} from '../types/maintenance';
import { authService } from './auth';

const API_BASE_URL = window.location.origin;

// ============================================================================
// Helper Functions
// ============================================================================

const getAuthHeaders = () => {
  const token = authService.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || error.message || 'An error occurred');
  }
  return response.json();
};

const buildQueryString = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

// ============================================================================
// Contract Template API
// ============================================================================

export const contractTemplateApi = {
  list: async (): Promise<PaginatedResponse<MaintenanceContractTemplate>> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/templates/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  get: async (id: number): Promise<MaintenanceContractTemplate> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/templates/${id}/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data: MaintenanceContractTemplateForm): Promise<MaintenanceContractTemplate> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/templates/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: Partial<MaintenanceContractTemplateForm>): Promise<MaintenanceContractTemplate> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/templates/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/templates/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to delete template');
    }
  },
};

// ============================================================================
// Contract API
// ============================================================================

export const contractApi = {
  list: async (filters?: ContractFilters): Promise<PaginatedResponse<MaintenanceContractListItem>> => {
    const queryString = filters ? buildQueryString(filters as Record<string, unknown>) : '';
    const url = `${API_BASE_URL}/api/maintenance/contracts/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  get: async (id: number): Promise<MaintenanceContract> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/contracts/${id}/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data: MaintenanceContractForm): Promise<MaintenanceContract> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/contracts/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: Partial<MaintenanceContractForm>): Promise<MaintenanceContract> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/contracts/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/contracts/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to delete contract');
    }
  },

  createAppointment: async (id: number, data: MaintenanceAppointmentForm): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/contracts/${id}/create-appointment/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ============================================================================
// Appointment API
// ============================================================================

export const appointmentApi = {
  list: async (filters?: AppointmentFilters): Promise<PaginatedResponse<MaintenanceAppointmentListItem>> => {
    const queryString = filters ? buildQueryString(filters as Record<string, unknown>) : '';
    const url = `${API_BASE_URL}/api/maintenance/appointments/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  get: async (id: number): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data: MaintenanceAppointmentForm): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: Partial<MaintenanceAppointmentForm>): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  myAppointments: async (filters?: {
    status?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedResponse<MaintenanceAppointmentListItem>> => {
    const queryString = filters ? buildQueryString(filters) : '';
    const url = `${API_BASE_URL}/api/maintenance/appointments/my-appointments/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  scheduled: async (filters?: {
    date_from?: string;
    date_to?: string;
    include_completed?: boolean;
  }): Promise<MaintenanceAppointmentCalendarItem[]> => {
    const queryString = filters ? buildQueryString(filters as Record<string, unknown>) : '';
    const url = `${API_BASE_URL}/api/maintenance/appointments/scheduled/${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  checkin: async (id: number, data: GPSCheckinData): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/checkin/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  checkout: async (id: number, data: GPSCheckoutData): Promise<MaintenanceAppointment> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/checkout/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  getPhotos: async (id: number): Promise<MaintenancePhoto[]> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/photos/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  addPhoto: async (id: number, data: FormData): Promise<MaintenancePhoto> => {
    const token = authService.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/photos/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: data,
    });
    return handleResponse(response);
  },

  getRepairItems: async (id: number): Promise<MaintenanceRepairItem[]> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/repair-items/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  addRepairItem: async (id: number, data: MaintenanceRepairItemForm): Promise<MaintenanceRepairItem> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/repair-items/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  createEstimate: async (id: number, data?: {
    repair_item_ids?: number[];
    include_all?: boolean;
  }): Promise<CreateEstimateResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/create-estimate/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data || { include_all: true }),
    });
    return handleResponse(response);
  },

  regenerateReport: async (id: number): Promise<{ message: string }> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/appointments/${id}/regenerate-report/`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};

// ============================================================================
// Photo API
// ============================================================================

export const photoApi = {
  get: async (id: number): Promise<MaintenancePhoto> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/photos/${id}/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: Partial<MaintenancePhotoForm>): Promise<MaintenancePhoto> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/photos/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/photos/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to delete photo');
    }
  },
};

// ============================================================================
// Repair Item API
// ============================================================================

export const repairItemApi = {
  get: async (id: number): Promise<MaintenanceRepairItem> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/repair-items/${id}/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  update: async (id: number, data: Partial<MaintenanceRepairItemForm>): Promise<MaintenanceRepairItem> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/repair-items/${id}/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/repair-items/${id}/`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to delete repair item');
    }
  },

  markRepaired: async (id: number, repair_notes?: string): Promise<MaintenanceRepairItem> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/repair-items/${id}/mark-repaired/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ repair_notes }),
    });
    return handleResponse(response);
  },
};

// ============================================================================
// Notification Settings API
// ============================================================================

export const notificationSettingsApi = {
  get: async (): Promise<MaintenanceNotificationSettings> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/notification-settings/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  update: async (data: MaintenanceNotificationSettingsForm): Promise<MaintenanceNotificationSettings> => {
    const response = await fetch(`${API_BASE_URL}/api/maintenance/notification-settings/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Export all APIs as a single object for convenience
export const maintenanceApi = {
  templates: contractTemplateApi,
  contracts: contractApi,
  appointments: appointmentApi,
  photos: photoApi,
  repairItems: repairItemApi,
  notificationSettings: notificationSettingsApi,
};

export default maintenanceApi;
